import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const AlumniCarousel = ({ autoScroll = true, scrollSpeed = 0.8 }) => {
  const navigate = useNavigate();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const animationRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    fetchAlumni();
  }, []);

  useEffect(() => {
    if (autoScroll && alumni.length > 0 && scrollContainerRef.current && !isDragging) {
      startAutoScroll();
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [alumni, autoScroll, isDragging]);

  const fetchAlumni = async () => {
    try {
      const response = await api.get('/alumni');
      console.log('Fetched alumni:', response.data);
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
    } finally {
      setLoading(false);
    }
  };

  const startAutoScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollAmount = container.scrollLeft;
    
    const scroll = () => {
      if (container && !isDragging) {
        scrollAmount += scrollSpeed;
        container.scrollLeft = scrollAmount;
        
        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 1) {
          scrollAmount = 0;
          container.scrollLeft = 0;
        }
        animationRef.current = requestAnimationFrame(scroll);
      }
    };
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(scroll);
  };

  const stopAutoScroll = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  // Mouse/Touch drag scrolling handlers
  const handleMouseDown = (e) => {
    stopAutoScroll();
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = 'grab';
    if (autoScroll) {
      startAutoScroll();
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      scrollContainerRef.current.style.cursor = 'grab';
      if (autoScroll) {
        startAutoScroll();
      }
    }
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    stopAutoScroll();
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (autoScroll) {
      startAutoScroll();
    }
  };

  const handleCardClick = (id) => {
    if (!isDragging) {
      navigate(`/alumni/${id}`);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'Chairman':
        return 'bg-yellow-100 text-yellow-800';
      case 'Vice Chairman':
        return 'bg-orange-100 text-orange-800';
      case 'Secretary':
        return 'bg-blue-100 text-blue-800';
      case 'Treasurer':
        return 'bg-green-100 text-green-800';
      case 'Patron':
        return 'bg-purple-100 text-purple-800';
      case 'Advisor':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    if (photoUrl.startsWith('/uploads')) {
      return `https://graduates.katinetti.ac.ke${photoUrl}`;
    }
    return `https://graduates.katinetti.ac.ke/uploads/alumni/${photoUrl}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (alumni.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">👥</div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">No Alumni Found</h3>
        <p className="text-gray-600">Check back later to meet our distinguished alumni.</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <div 
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="overflow-x-auto scrollbar-hide select-none"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
          overflowX: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        <style>
          {`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <div className="flex gap-6" style={{ width: 'max-content' }}>
          {alumni.map((alumnus) => {
            const imageUrl = getImageUrl(alumnus.photo_url);
            
            return (
              <div
                key={alumnus.id}
                onClick={() => handleCardClick(alumnus.id)}
                className="cursor-pointer transform transition-all duration-300 hover:-translate-y-2 flex-shrink-0"
                style={{ width: '300px' }}
              >
                {/* Card with same styling as Alumni Page */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow h-full overflow-hidden flex flex-col">
                  {/* Image Section - Takes full width */}
                  <div className="w-full h-72 overflow-hidden bg-gray-100 flex-shrink-0">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={alumnus.name}
                        className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          console.error(`Failed to load image: ${imageUrl}`);
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(alumnus.name)}&background=1e3a8a&color=fff&size=200`;
                        }}
                        draggable="false"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                        <span className="text-white text-5xl font-bold">
                          {alumnus.name?.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Details Section - Same as Alumni Page */}
                  <div className="p-4 text-left flex-grow">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h3 className="font-semibold text-gray-800 text-base truncate">
                        {alumnus.name}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getRoleBadgeClass(alumnus.role)}`}>
                        {alumnus.role}
                      </span>
                    </div>
                    
                    {alumnus.department_name && (
                      <p className="text-sm text-gray-500 mb-2 truncate">{alumnus.department_name}</p>
                    )}
                    
                    {alumnus.graduation_year && (
                      <p className="text-xs text-gray-400 mb-2">Class of {alumnus.graduation_year}</p>
                    )}
                    
                    <div className="mt-3 text-primary text-sm font-medium">
                      View Profile →
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AlumniCarousel;
